
import { UserProfile } from '../types';

export type AppLanguage = 'en' | 'es' | 'nl';
export const LANGUAGE_STORAGE_KEY = 'ha_language';
export const LANGUAGE_PICKED_KEY = 'ha_language_picked';

export const getAppLanguage = (profile?: UserProfile | null): AppLanguage => {
  if (profile?.language === 'es') return 'es';
  if (profile?.language === 'nl') return 'nl';
  const stored = typeof window !== 'undefined' ? localStorage.getItem(LANGUAGE_STORAGE_KEY) : null;
  if (stored === 'es') return 'es';
  if (stored === 'nl') return 'nl';
  return 'en';
};

export const getTranslation = (profile: UserProfile | null | undefined) => {
  const lang = getAppLanguage(profile);

  const strings = {
    en: {
      // Nav
      features: "Features",
      howItWorks: "How It Works",
      blog: "Blog",
      pricing: "Pricing",
      becomePartner: "Become a Partner",
      startNow: "Start Now",
      dashboard: "Dashboard",

      // Hero
      nowLive: "Now live — v2.5",
      heroTitle1: "THE AI",
      heroTitle2: "BASKETBALL",
      heroTitle3: "PLAYBOOK",
      heroTitle4: "& COACH BOARD",
      heroSubtitle: "The all-in-one coaching platform for modern basketball. Build drills, manage your squad, and analyse matches with AI.",
      startFree: "Start for free",
      lovedBy: "Loved by",
      coaches: "coaches",

      // Stats
      statCoaches: "Coaches",
      statDrills: "Drills Created",
      statCountries: "Countries",
      statRating: "App Rating",

      // Pillars
      pillar1Title: "Coach Hub",
      pillar1Desc: "Create professional diagrams and drills with AI. No more drawing on paper — generate plays in seconds.",
      pillar2Title: "Squad Management",
      pillar2Desc: "Attendance tracking, team messaging, and tactical assignments — all in one place for your whole roster.",
      pillar3Title: "Tournament Engine",
      pillar3Desc: "Manage brackets, live scores, and court allocation for any size event from local to national level.",

      // How it works
      simpleByDesign: "Simple by design",
      howItWorksTitle: "HOW IT",
      howItWorksTitleAccent: "WORKS.",
      step1Title: "Build Your Playbook",
      step1Desc: "Use AI or drag-and-drop to create professional diagrams for any play, drill, or set piece in seconds.",
      step2Title: "Organise Your Team",
      step2Desc: "Add players, track attendance, assign drills and send tactical briefings directly to your squad.",
      step3Title: "Analyse & Improve",
      step3Desc: "Review match footage, track player stats, and get AI-powered coaching insights after every game.",

      // Features section
      everythingYouNeed: "Everything you need",
      allTheToolsTitle: "ALL THE",
      allTheToolsAccent: "TOOLS.",
      allTheToolsSubtitle: "One platform, built end-to-end for basketball coaches at every level.",
      openInApp: "Open in app →",
      learnMore: "Learn more →",

      // Features
      f1Title: "AI Drill Creator",
      f1Desc: "Describe a drill in plain text and get a professional diagram in seconds. No design skills needed.",
      f1Detail: "Powered by AI, the Drill Creator turns plain language into complete, professional drill diagrams. Just type what you want — the AI handles spacing, player positions, arrows, and notes automatically.",
      f1b1: "Generate drills from text in seconds",
      f1b2: "Full court diagrams with arrows & players",
      f1b3: "Save to your personal drill library",
      f1b4: "Share directly with your team",

      f2Title: "Tournament Builder",
      f2Desc: "Manage brackets, live scores, and court distribution for any size event — from 4 to 64 teams.",
      f2Detail: "Run tournaments of any size without spreadsheets. The Tournament Builder handles bracket generation, real-time scoring, and court scheduling automatically.",
      f2b1: "Automatic bracket generation",
      f2b2: "Live score updates",
      f2b3: "Court scheduling & distribution",
      f2b4: "Shareable public bracket link",

      f3Title: "Playbook Vault",
      f3Desc: "Centralised storage for your entire playbook, accessible from any device, anytime.",
      f3Detail: "All your plays, drills and sets in one place. Organise by category, tag by level, and share specific sections with players or assistants.",
      f3b1: "Unlimited playbook storage",
      f3b2: "Organise by category & tags",
      f3b3: "Share with players or staff",
      f3b4: "Access from any device",

      f4Title: "Squad Hub",
      f4Desc: "Attendance tracking, direct messaging, and drill assignments all in one place for your whole team.",
      f4Detail: "Manage your full roster without switching apps. Track attendance, send messages, assign drills, and monitor player progress — all from one dashboard.",
      f4b1: "Roster & attendance tracking",
      f4b2: "Direct team messaging",
      f4b3: "Drill assignments per player",
      f4b4: "Training session history",

      f5Title: "Match Analysis",
      f5Desc: "Upload footage and get AI-powered breakdowns of performance, spacing, and defensive patterns.",
      f5Detail: "Upload match footage and let AI analyse spacing, rotations and defensive patterns. Highlight key moments and share clips directly with players.",
      f5b1: "AI-powered footage analysis",
      f5b2: "Spacing & rotation breakdowns",
      f5b3: "Clip creation & sharing",
      f5b4: "Defensive pattern recognition",

      f6Title: "Scrimmage Hub",
      f6Desc: "Post and find scrimmage requests in your area. Organise pickup games and practice matches with a few taps.",
      f6Detail: "Find opponents near you for scrimmages and practice games. Post availability, browse open requests, and confirm matches directly in the app.",
      f6b1: "Post & find scrimmage requests",
      f6b2: "Filter by location & level",
      f6b3: "Confirm and schedule matches",
      f6b4: "In-app team communication",

      f7Title: "Coach Hub",
      f7Desc: "Connect with other coaches, share knowledge, and follow community posts from the coaching world.",
      f7Detail: "A coaching community built into the platform. Share plays, ask questions, post insights and follow top coaches — all without leaving HoopsAtlas.",
      f7b1: "Community feed for coaches",
      f7b2: "Share plays & diagrams",
      f7b3: "Follow coaches & clubs",
      f7b4: "Discover coaching content",

      f8Title: "Club HQ",
      f8Desc: "Shared playbooks, multi-team management, and an admin console for your entire club under one roof.",
      f8Detail: "Built for clubs with multiple teams. Club HQ gives admins a central console to manage coaches, share playbooks across teams, and oversee all activity.",
      f8b1: "Multi-team management",
      f8b2: "Shared club playbook",
      f8b3: "Admin console & permissions",
      f8b4: "Club-wide drill library",

      f9Title: "Match Archive",
      f9Desc: "Store, label, and revisit your match recordings. Build a full video library of your season in one place.",
      f9Detail: "Every match saved, labelled, and searchable. Build a complete video archive of your season and revisit key moments whenever you need.",
      f9b1: "Season-long video library",
      f9b2: "Label & tag recordings",
      f9b3: "Searchable archive",
      f9b4: "Share clips with staff",

      f10Title: "Video Generator",
      f10Desc: "Turn your tactical diagrams into animated video clips automatically. Perfect for player briefings and reels.",
      f10Detail: "Instantly convert any play diagram into an animated video. Use it for pre-game briefings, social media content, or sending plays directly to players.",
      f10b1: "Auto-animate any diagram",
      f10b2: "Export as MP4 video",
      f10b3: "Use for social media reels",
      f10b4: "Send clips to players",

      // Testimonials
      testimonialLabel: "Coach testimonials",
      coachesLoveIt: "COACHES",
      coachesLoveItAccent: "LOVE IT.",
      t1Quote: "HoopsAtlas completely changed how I prepare my team. Building plays used to take hours — now it takes minutes.",
      t1Role: "U18 Head Coach",
      t2Quote: "The tournament builder alone is worth it. I ran a 16-team event and everything organised itself automatically.",
      t2Role: "Club Director",
      t3Quote: "My players actually understand the playbook now. The diagrams speak louder than any whiteboard session.",
      t3Role: "Assistant Coach",

      // Blog
      coachingBlog: "Coaching Blog",
      insightsTitle: "COACHING",
      insightsAccent: "INSIGHTS.",
      insightsSubtitle: "Perspectives on modern basketball strategy, data-driven coaching, and the digital playbook.",
      blog1Tag: "Strategy",
      blog1Date: "March 2026",
      blog1Title: "The Death of the Paper Playbook",
      blog1Desc: "In the modern era of basketball, speed is everything. The transition from physical clipboards to digital platforms represents a fundamental shift in how tactical information is created and shared.",
      blog2Tag: "Analytics",
      blog2Date: "March 2026",
      blog2Title: "Data-Driven Player Development",
      blog2Desc: "Data is only as good as your ability to interpret it. By tracking KPIs and growth metrics, coaches get actionable insight previously reserved for elite-level staffs.",
      blog3Tag: "Culture",
      blog3Date: "March 2026",
      blog3Title: "Building Club Culture in the Digital Age",
      blog3Desc: "A club is more than a collection of players — it's a shared tactical identity. In the digital age, maintaining that identity across multiple age groups requires a centralised source of truth.",
      readMore: "Read more",
      viewAllPosts: "View all posts",

      // Pricing
      pricingLabel: "Pricing",
      pricingTitle: "CHOOSE YOUR",
      pricingAccent: "PLAN.",
      pricingSubtitle: "Start free. Upgrade when you're ready to take your coaching to the next level.",
      freeTier: "Free",
      noCreditCard: "No credit card required",
      billedMonthly: "Billed monthly, cancel anytime",
      customPricing: "Custom pricing for large clubs",
      getStartedFree: "Get Started Free",
      getPro: "Get Pro",
      getUltra: "Get Ultra",
      getClub: "Get Club Access",
      mostPopular: "Most Popular",
      proFeature1: "Unlimited drills",
      proFeature2: "AI Coach",
      proFeature3: "HD social exports",
      proFeature4: "Video creation",
      proFeature5: "Match analysis",
      proFeature6: "Priority support",
      ultraFeature1: "8 hrs AI game analysis/mo",
      ultraFeature2: "Automatic team & player insights",
      ultraFeature3: "Offensive & defensive analysis",
      ultraFeature4: "Key moments & coaching points",
      ultraFeature5: "Match reports with improvement areas",
      clubFeature1: "10–unlimited licences",
      clubFeature2: "Shared playbook",
      clubFeature3: "Admin console",
      clubFeature4: "24/7 priority support",
      clubFeature5: "White-label reports",
      freeFeature1: "1 drill",
      freeFeature2: "Team access",
      freeFeature3: "Manual export",
      freeFeature4: "Community access",
      saveMonths: "Save 2 months",

      // CTA
      timeToWin: "TIME TO",
      timeToWinAccent: "WIN.",
      ctaSubtitle: "Stop drawing on paper. Get your team organised, your playbook digital, and your coaching data-driven — for free.",
      noCreditCardRequired: "No credit card required · Free forever plan available",

      // Footer
      footerDesc: "The coaching platform for modern basketball organisations. Est. 2026, Belgium.",
      footerBasketball: "Basketball",
      footerPlatform: "Platform",
      footerPlays: "Basketball Plays",
      footerOffense: "Offense Plays",
      footerDefense: "Defense Plays",
      footerInbounds: "Inbound Plays",
      footerBlog: "Coaching Blog",
      footerCoachBoard: "Coach Board",
      footerAICoach: "AI Basketball Coach",
      footerPlaybookCreator: "Playbook Creator",
      footerAbout: "About HoopsAtlas",
      footerPrivacy: "Privacy Policy",
      footerPrivacyShort: "Privacy",
      footerTerms: "Terms",
      footerCopyright: "HOOPSATLAS © 2026 · BELGIUM",

      // App store
      getItOn: "Get it on",
      googlePlay: "Google Play",

      // Generic
      home: "Home",
      library: "Library",
      training: "Training",
      playbook: "Playbook",
      player: "Player",
      coach: "Coach",
      club: "Club",
      parent: "Parent",
      squad: "Squad",
      match: "Match",
      create: "Create",
      back: "Back",
      backToHome: "Back to Home",

      // PageLayout
      playsLibrary: "Plays Library",
      plPageFooterDesc: "The AI basketball playbook and coaching platform for coaches at every level.",

      // AboutPage
      aboutReturnHQ: "Return to HQ",
      aboutProtocol: "Protocol: Full Access",
      aboutBriefing: "Tactical Briefing",
      aboutHeroTitle: "FULL SYSTEM OVERVIEW.",
      aboutHeroAccent: "OVERVIEW.",
      aboutHeroDesc: "HoopsAtlas is not just an app; it's a digital ecosystem for basketball intelligence. From AI-driven tactics to real-time tournament management.",
      aboutOriginTitle: "About HoopsAtlas",
      aboutOriginSub: "The origin of our tactical directive",
      aboutStory1: "HoopsAtlas didn't start as a \"business idea\" — it started as a solution to a problem I had every week as a coach.",
      aboutStory2: "I'm Stan, a basketball coach and player. Before each practice, I was constantly doing the same things: searching for drills, redrawing plays, pulling plans together from notes, photos, and different apps. It took time — and it wasn't organized.",
      aboutStory3: "So I decided to build the tool I actually wanted to use: one platform where coaches can organize their playbook, drills, and practice plans — always accessible when you need it. That became HoopsAtlas.",
      aboutStory4: "Today, coaches use HoopsAtlas to plan faster, communicate more clearly with their team, and build structure throughout the season — without the chaos.",
      aboutMethodLabel: "The HoopsAtlas Methodology",
      aboutMethodTitle: "REDEFINING ",
      aboutMethodAccent: "THE GAME.",
      aboutS1Title: "01. Spacing Geometry",
      aboutS1P1: "Modern basketball is a game of inches and angles. Our methodology focuses on the \"Five-Out\" and \"Four-Out\" spacing principles that dominate the professional game today. By utilizing AI to analyze player positioning, we help coaches identify the exact moments where spacing collapses, leading to turnovers or contested shots.",
      aboutS1P2: "We believe that every offensive set should create at least three distinct scoring threats simultaneously. Our tactical engine is programmed to recognize these threats and suggest movements that maximize defensive stress.",
      aboutS2Title: "02. Decision-Based Training",
      aboutS2P1: "Drills shouldn't just be about repetition; they should be about reading the game. HoopsAtlas promotes \"Small Sided Games\" (SSGs) that force players to make rapid tactical decisions under pressure. Our drill library is categorized by the specific \"reads\" they develop—from pick-and-roll coverage to weak-side rotations.",
      aboutS2P2: "By integrating these drills into a cohesive season plan, coaches can ensure that their team's basketball IQ grows alongside their physical skills. The digital playbook allows for instant sharing of these concepts, ensuring everyone is aligned before they even step on the court.",
      aboutS3Title: "03. Data-Driven Feedback",
      aboutS3P1: "Video is the ultimate truth-teller. Our Atlas Vision engine translates raw footage into actionable data points. We focus on \"Effective Field Goal Percentage\" (eFG%) and \"Paint Touches\" as the primary indicators of offensive health.",
      aboutS3P2: "Instead of subjective observations, coaches can now present players with objective evidence of their performance. This data-driven approach builds trust and accelerates the learning curve for athletes at all levels.",
      aboutS4Title: "04. The Digital HQ Concept",
      aboutS4P1: "A coach's mind is often cluttered with plays, schedules, and player stats. HoopsAtlas serves as a \"Second Brain,\" externalizing this information into a secure, organized digital headquarters. This reduces cognitive load and allows coaches to focus on what they do best: teaching the game.",
      aboutS4P2: "From the \"Locker Room\" chat to the \"Tactical Vault,\" every feature is designed to streamline the administrative side of coaching, giving you more time to focus on your players and your strategy.",
      aboutFC1Sub: "AI Synthesis",
      aboutFC1Title: "Magic Coach",
      aboutFC1Desc: "Describe your tactics in text and let AI instantly generate professional diagrams and step-by-step plans. No more hours spent drawing on a clipboard.",
      aboutFC2Sub: "Vision Engine",
      aboutFC2Title: "Atlas Vision",
      aboutFC2Desc: "Upload match footage. Our AI analyzes spacing, shot quality, and defensive rotations to create an in-depth tactical report.",
      aboutFC3Sub: "Competition",
      aboutFC3Title: "Tournament Builder",
      aboutFC3Desc: "Manage complete tournaments. The engine handles pools, schedules for multiple courts, and tracks live standings and scores.",
      aboutFC4Sub: "Social Content",
      aboutFC4Title: "Social Studio",
      aboutFC4Desc: "Translate tactics into viral success. Export professional graphics and videos of your plays, optimized for TikTok and Instagram Reels.",
      aboutFC5Sub: "Movement",
      aboutFC5Title: "Motion Engine",
      aboutFC5Desc: "Convert static diagrams into smooth HD video animations. Players see exactly how the movement lines and actions flow into each other.",
      aboutFC6Sub: "Cloud Vault",
      aboutFC6Title: "Digital Playbook",
      aboutFC6Desc: "Your complete tactical archive in the cloud. Create playbooks for specific teams and sync them instantly with all coaches and players.",
      aboutFC7Sub: "Organization",
      aboutFC7Title: "Club Headquarters",
      aboutFC7Desc: "For clubs: Manage your entire staff. Sponsor coaches for Pro features and share central tactical systems across your entire organization.",
      aboutFC8Sub: "Logistics",
      aboutFC8Title: "Squad Hub",
      aboutFC8Desc: "Manage your team rosters, track attendance for training sessions, plan events, and communicate via the secure Locker Room chat.",
      aboutFC9Sub: "Navigation",
      aboutFC9Title: "Tactical Grounds",
      aboutFC9Desc: "Find every basketball court nearby. Use AI and Maps to discover both public outdoor courts and indoor sports halls in your sector.",
      aboutTechTitle: "Hardware & API",
      aboutTechTitleAccent: "Integration",
      aboutTechSub: "Built on elite infrastructure",
      aboutTechCore: "Core Tactical Reasoning",
      aboutTechGateway: "Secure License Gateway",
      aboutTechStorage: "Military-Grade Storage",
      aboutTechGeo: "Sector Geo-Grounding",
      aboutCTATitle: "START COMMANDING.",
      aboutCTADesc: "Stop drawing on paper. Activate your digital headquarters and dominate the competition with data and AI.",
      aboutCTABtn: "Initialize Core HQ",
      aboutModuleActive: "Module Active",

      // BasketballPlaysPage
      bpFreeLibrary: "Free Plays Library",
      bpHeroTitle: "Basketball ",
      bpHeroAccent: "Plays",
      bpHeroTitle2: " Library",
      bpHeroDesc: "+ free basketball plays for coaches at every level — offense sets, defense schemes, and inbound plays. Draw any play on the HoopsAtlas coach board in seconds.",
      bpDrawFree: "Draw a Play Free",
      bpBuildPlaybook: "Build Your Playbook",
      bpOffenseLabel: "Offense Plays",
      bpOffenseDesc: "Half-court sets, motion offenses, pick-and-roll actions and transition plays.",
      bpDefenseLabel: "Defense Plays",
      bpDefenseDesc: "Man-to-man, zone, press schemes and hybrid systems to stop any offense.",
      bpInboundsLabel: "Inbound Plays",
      bpInboundsDesc: "BLOB and SLOB sets designed to score from out-of-bounds situations.",
      bpAllPlays: "All ",
      bpAllPlaysAccent: "Plays",
      bpCTATitle: "Draw Any Play",
      bpCTAAccent: "In Seconds.",
      bpCTADesc: "Use the HoopsAtlas coach board to diagram any play, build your playbook, and share it with your team — all in one place.",
      bpStartFree: "Start for Free",
      bpPlays: "plays",

      // BlogPage
      blogCoachingBlog: "Coaching Blog",
      blogHeroTitle: "Basketball ",
      blogHeroAccent: "Coaching",
      blogHeroTitle2: " Insights",
      blogHeroDesc: "Guides, play breakdowns, and coaching tips for basketball coaches at every level — from youth to semi-pro.",
      blogReadArticle: "Read Article",
      blogRead: "Read",
      blogCTATitle: "Ready to try HoopsAtlas?",
      blogCTADesc: "Draw plays, build your playbook, and generate AI drills — free for any coach.",
      blogStartFree: "Start for Free",

      // CoachBoardPage
      cbDigitalCoach: "Digital Coach Board",
      cbHeroTitle: "The Digital",
      cbHeroAccent: "Coach Board",
      cbHeroTitle2: "for Basketball",
      cbHeroDesc: "Draw basketball plays, diagram defensive schemes, and build your playbook on an interactive digital court — faster and clearer than any whiteboard or paper diagram.",
      cbOpenFree: "Open Coach Board Free",
      cbNoDownload: "No download required · Works on any device · Free plan available",
      cbFeaturesTitle: "Everything on one ",
      cbFeaturesAccent: "board.",
      cbF1Title: "Full Interactive Court",
      cbF1Desc: "Half-court and full-court views with all standard markings. Draw on a real basketball court, not a blank canvas.",
      cbF2Title: "Smart Drawing Tools",
      cbF2Desc: "Passes, dribbles, cuts, screens, and player routes — all standard basketball symbols, available in one tap.",
      cbF3Title: "Numbered Players",
      cbF3Desc: "Place offensive and defensive players by position (1–5). Add names for personalised player assignments.",
      cbF4Title: "Save to Your Playbook",
      cbF4Desc: "Every play you draw is saved directly to your digital playbook, organised and accessible from any device.",
      cbF5Title: "Share Instantly",
      cbF5Desc: "Export your play diagram as an image and share it with your team via WhatsApp, email, or in the app.",
      cbF6Title: "AI Play Generator",
      cbF6Desc: "Describe a play in plain text and the AI generates the diagram for you — in seconds.",
      cbWhyTitle: "Why coaches switch to a ",
      cbWhyAccent: "digital board",
      cbWhy1Title: "Paper gets lost",
      cbWhy1Desc: "A digital play stays in your playbook permanently. Search, edit, and share in seconds.",
      cbWhy2Title: "Whiteboards can't be shared",
      cbWhy2Desc: "Every diagram you draw on HoopsAtlas can be shared with your full team instantly.",
      cbWhy3Title: "Static diagrams miss context",
      cbWhy3Desc: "Add coaching notes, player roles, and read options directly alongside each play.",
      cbWhy4Title: "Your playbook lives in the app",
      cbWhy4Desc: "Every play you draw is saved, organised, and accessible on any device.",
      cbBoardDesc: "Interactive half-court · Draw plays in seconds · Share with one tap",
      cbTryFree: "Try It Free",
      cbRelated: "Related Features",
      cbRelated1Label: "AI Basketball Coach",
      cbRelated1Desc: "Generate drills and plays with AI in seconds.",
      cbRelated2Label: "Playbook Creator",
      cbRelated2Desc: "Build and organise your complete team playbook.",
      cbRelated3Label: "Free Plays Library",
      cbRelated3Desc: "35+ free basketball plays to draw and use.",

      // AICoachPage
      aiFeature: "AI Feature",
      aiHeroTitle: "AI Basketball",
      aiHeroAccent: "Coach",
      aiHeroTitle2: "& Drill Generator",
      aiHeroDesc: "Describe what you need in plain text and the HoopsAtlas AI generates a complete basketball play diagram or drill — in seconds. No drawing required.",
      aiTryFree: "Try AI Coach Free",
      aiHowTitle: "How it ",
      aiHowAccent: "works.",
      aiStep1Title: "Describe Your Play or Drill",
      aiStep1Desc: "Type what you want in plain English — \"a pick and roll with a corner shooter\" or \"a defensive closeout drill for beginners\".",
      aiStep2Title: "AI Generates the Content",
      aiStep2Desc: "The AI understands basketball tactics and creates a complete diagram or step-by-step drill with coaching cues.",
      aiStep3Title: "Edit, Save and Share",
      aiStep3Desc: "Adjust the output on the coach board, save it to your playbook, and share it with your team instantly.",
      aiWhatTitle: "What you can ",
      aiWhatAccent: "generate.",
      aiYouType: "You type:",
      aiGenerates: "AI generates:",
      aiEx1Prompt: "\"A full-court press with a trap at half court\"",
      aiEx1Result: "Generates a complete 2-2-1 press diagram with trap zones and rotation arrows.",
      aiEx2Prompt: "\"Pick and roll from the top with a corner shooter\"",
      aiEx2Result: "Produces a PnR diagram with the roll, the corner spacing, and the read options labelled.",
      aiEx3Prompt: "\"A drill to improve defensive footwork for U14 players\"",
      aiEx3Result: "Returns a step-by-step drill with timing, setup instructions, and coaching cues.",
      aiBuiltTitle: "Built for every ",
      aiBuiltAccent: "coach.",
      aiUC1Role: "Youth Coaches",
      aiUC1Desc: "Generate age-appropriate drills instantly. No need to search YouTube for 30 minutes — describe what skill you're targeting and get a ready-to-run drill.",
      aiUC2Role: "Club Coaches",
      aiUC2Desc: "Build a consistent tactical system across multiple age groups. Use AI to generate plays that fit your system and adapt them for different age categories.",
      aiUC3Role: "School Coaches",
      aiUC3Desc: "Prepare practice sessions in minutes. Let AI handle the drill design while you focus on the coaching.",
      aiUC4Role: "Pro & Semi-Pro Coaches",
      aiUC4Desc: "Use AI to draft initial play concepts, then refine on the coach board. Faster preparation, more time for analysis.",
      aiCTATitle: "Your AI",
      aiCTAAccent: "Basketball Coach",
      aiCTATitle2: "is ready.",
      aiCTADesc: "Generate plays, drills, and practice plans in seconds. Free to try — no credit card required.",
      aiStartFree: "Start for Free",
      aiLink1Label: "Coach Board",
      aiLink1Desc: "Draw and edit any play on the interactive court.",
      aiLink2Label: "Playbook Creator",
      aiLink2Desc: "Organise all your plays in one digital playbook.",
      aiLink3Label: "Free Plays Library",
      aiLink3Desc: "Browse 35+ ready-to-use basketball plays.",

      // PlaybookCreatorPage
      pbDigital: "Digital Playbook",
      pbHeroTitle: "Basketball",
      pbHeroAccent: "Playbook",
      pbHeroTitle2: "Creator",
      pbHeroDesc: "Build a complete digital basketball playbook — draw your plays, organise them by category, and share the entire playbook with your team in one tap.",
      pbCreateFree: "Create Your Playbook Free",
      pbFreePlan: "Free plan available · No download required · Works on iOS, Android, and web",
      pbWholeTitle: "Your whole system",
      pbWholeAccent: "in one place.",
      pbF1Title: "Organise by Category",
      pbF1Desc: "Sort plays into offense, defense, BLOB, SLOB, and end-of-game. Find any play in seconds.",
      pbF2Title: "Draw on the Coach Board",
      pbF2Desc: "Every play is drawn on an interactive half-court with standard basketball symbols.",
      pbF3Title: "AI-Assisted Creation",
      pbF3Desc: "Describe a play in text and AI generates the diagram. Refine it on the board.",
      pbF4Title: "Assign to Players",
      pbF4Desc: "Tag plays to specific players or positions so everyone knows their role.",
      pbF5Title: "Share With Your Team",
      pbF5Desc: "Send the full playbook or individual plays to players via the app or as exports.",
      pbF6Title: "Private and Secure",
      pbF6Desc: "Your playbook is private by default. Share only what you want, with who you want.",
      pbStepsTitle: "Build your playbook",
      pbStepsAccent: "in 4 steps.",
      pbStep1Title: "Define your system",
      pbStep1Desc: "Choose your offensive and defensive foundation. Your playbook should reflect one clear tactical identity.",
      pbStep2Title: "Draw your 10 core plays",
      pbStep2Desc: "Start with the plays you run every game: 2-3 half-court sets, 1 BLOB, 1 SLOB, 1 quick hitter. Master those before adding more.",
      pbStep3Title: "Add coaching notes to each play",
      pbStep3Desc: "Every play gets 3-5 coaching points explaining the key reads and decision points.",
      pbStep4Title: "Share with your team",
      pbStep4Desc: "Send the full playbook to your squad. Players can review plays before training and access them during games.",
      pbLearnMore: "Learn more",
      pbLink1Label: "How to Create a Playbook",
      pbLink1Desc: "Step-by-step guide for building your first playbook.",
      pbLink2Label: "Coach Board",
      pbLink2Desc: "Draw plays on the interactive digital court.",
      pbLink3Label: "Free Plays Library",
      pbLink3Desc: "Browse 35+ basketball plays to add to your playbook.",
    },
    es: {
      // Nav
      features: "Funciones",
      howItWorks: "Cómo funciona",
      blog: "Blog",
      pricing: "Precios",
      becomePartner: "Hazte socio",
      startNow: "Comenzar",
      dashboard: "Panel",

      // Hero
      nowLive: "Disponible ahora — v2.5",
      heroTitle1: "EL",
      heroTitle2: "CUADERNO",
      heroTitle3: "BALONCESTO IA",
      heroTitle4: "& PIZARRA TÁCTICA",
      heroSubtitle: "La plataforma de entrenamiento todo en uno para el baloncesto moderno. Crea ejercicios, gestiona tu equipo y analiza partidos con IA.",
      startFree: "Empieza gratis",
      lovedBy: "Amado por",
      coaches: "entrenadores",

      // Stats
      statCoaches: "Entrenadores",
      statDrills: "Ejercicios creados",
      statCountries: "Países",
      statRating: "Valoración App",

      // Pillars
      pillar1Title: "Coach Hub",
      pillar1Desc: "Crea diagramas y ejercicios profesionales con IA. Sin más dibujos en papel — genera jugadas en segundos.",
      pillar2Title: "Gestión de Equipo",
      pillar2Desc: "Control de asistencia, mensajería y asignación de ejercicios — todo en un solo lugar para tu plantilla.",
      pillar3Title: "Motor de Torneos",
      pillar3Desc: "Gestiona llaves, marcadores en vivo y asignación de pistas para eventos de cualquier tamaño.",

      // How it works
      simpleByDesign: "Simple por diseño",
      howItWorksTitle: "CÓMO",
      howItWorksTitleAccent: "FUNCIONA.",
      step1Title: "Crea tu Cuaderno",
      step1Desc: "Usa IA o arrastra y suelta para crear diagramas profesionales de cualquier jugada o ejercicio en segundos.",
      step2Title: "Organiza tu Equipo",
      step2Desc: "Agrega jugadores, controla asistencia, asigna ejercicios y envía informes tácticos directamente a tu equipo.",
      step3Title: "Analiza y Mejora",
      step3Desc: "Revisa grabaciones de partidos, sigue estadísticas y obtén consejos de entrenamiento basados en IA.",

      // Features section
      everythingYouNeed: "Todo lo que necesitas",
      allTheToolsTitle: "TODAS LAS",
      allTheToolsAccent: "HERRAMIENTAS.",
      allTheToolsSubtitle: "Una plataforma construida de extremo a extremo para entrenadores de baloncesto de todos los niveles.",
      openInApp: "Abrir en la app →",
      learnMore: "Saber más →",

      // Features
      f1Title: "Creador de Ejercicios IA",
      f1Desc: "Describe un ejercicio en texto simple y obtén un diagrama profesional en segundos. Sin conocimientos de diseño.",
      f1Detail: "Con IA, el Creador de Ejercicios convierte lenguaje natural en diagramas completos y profesionales. Solo escribe lo que quieres — la IA se encarga del espaciado, posiciones, flechas y notas automáticamente.",
      f1b1: "Genera ejercicios desde texto en segundos",
      f1b2: "Diagramas de cancha completa con flechas y jugadores",
      f1b3: "Guarda en tu biblioteca personal",
      f1b4: "Comparte directamente con tu equipo",

      f2Title: "Constructor de Torneos",
      f2Desc: "Gestiona llaves, marcadores en vivo y distribución de canchas para cualquier evento — de 4 a 64 equipos.",
      f2Detail: "Organiza torneos de cualquier tamaño sin hojas de cálculo. El Constructor de Torneos genera llaves, actualiza marcadores y programa canchas automáticamente.",
      f2b1: "Generación automática de llaves",
      f2b2: "Actualización de marcadores en vivo",
      f2b3: "Programación y distribución de canchas",
      f2b4: "Enlace público de llave compartible",

      f3Title: "Bóveda de Cuadernos",
      f3Desc: "Almacenamiento centralizado de todo tu cuaderno, accesible desde cualquier dispositivo, en cualquier momento.",
      f3Detail: "Todas tus jugadas, ejercicios y sets en un solo lugar. Organiza por categoría, etiqueta por nivel y comparte secciones específicas con jugadores o asistentes.",
      f3b1: "Almacenamiento ilimitado de cuadernos",
      f3b2: "Organiza por categoría y etiquetas",
      f3b3: "Comparte con jugadores o staff",
      f3b4: "Accede desde cualquier dispositivo",

      f4Title: "Hub del Equipo",
      f4Desc: "Control de asistencia, mensajería directa y asignación de ejercicios en un solo lugar para todo tu equipo.",
      f4Detail: "Gestiona toda tu plantilla sin cambiar de app. Controla asistencia, envía mensajes, asigna ejercicios y monitorea el progreso de jugadores — todo desde un panel.",
      f4b1: "Control de plantilla y asistencia",
      f4b2: "Mensajería directa al equipo",
      f4b3: "Asignación de ejercicios por jugador",
      f4b4: "Historial de sesiones de entrenamiento",

      f5Title: "Análisis de Partidos",
      f5Desc: "Sube grabaciones y obtén análisis de rendimiento, espaciado y patrones defensivos con IA.",
      f5Detail: "Sube grabaciones y deja que la IA analice espaciado, rotaciones y patrones defensivos. Destaca momentos clave y comparte clips directamente con los jugadores.",
      f5b1: "Análisis de grabaciones con IA",
      f5b2: "Análisis de espaciado y rotaciones",
      f5b3: "Creación y compartición de clips",
      f5b4: "Reconocimiento de patrones defensivos",

      f6Title: "Hub de Scrimmages",
      f6Desc: "Publica y encuentra solicitudes de scrimmage en tu zona. Organiza juegos y partidos de práctica con pocos toques.",
      f6Detail: "Encuentra rivales cerca para scrimmages y partidos de práctica. Publica disponibilidad, navega solicitudes abiertas y confirma partidos directamente en la app.",
      f6b1: "Publica y encuentra scrimmages",
      f6b2: "Filtra por ubicación y nivel",
      f6b3: "Confirma y programa partidos",
      f6b4: "Comunicación de equipo en la app",

      f7Title: "Coach Hub",
      f7Desc: "Conecta con otros entrenadores, comparte conocimiento y sigue publicaciones de la comunidad.",
      f7Detail: "Una comunidad de entrenadores integrada en la plataforma. Comparte jugadas, haz preguntas, publica insights y sigue a los mejores entrenadores — sin salir de HoopsAtlas.",
      f7b1: "Feed de comunidad para entrenadores",
      f7b2: "Comparte jugadas y diagramas",
      f7b3: "Sigue entrenadores y clubes",
      f7b4: "Descubre contenido de entrenamiento",

      f8Title: "Club HQ",
      f8Desc: "Cuadernos compartidos, gestión multiequipo y consola de administración para todo tu club bajo un mismo techo.",
      f8Detail: "Diseñado para clubes con múltiples equipos. Club HQ da a los administradores una consola central para gestionar entrenadores, compartir cuadernos y supervisar toda la actividad.",
      f8b1: "Gestión de múltiples equipos",
      f8b2: "Cuaderno compartido del club",
      f8b3: "Consola de admin y permisos",
      f8b4: "Biblioteca de ejercicios del club",

      f9Title: "Archivo de Partidos",
      f9Desc: "Almacena, etiqueta y revisa tus grabaciones de partidos. Construye una videoteca completa de tu temporada.",
      f9Detail: "Cada partido guardado, etiquetado y buscable. Construye un archivo de video completo de tu temporada y revisa momentos clave cuando lo necesites.",
      f9b1: "Videoteca de toda la temporada",
      f9b2: "Etiqueta y clasifica grabaciones",
      f9b3: "Archivo buscable",
      f9b4: "Comparte clips con el staff",

      f10Title: "Generador de Video",
      f10Desc: "Convierte tus diagramas tácticos en clips de video animados automáticamente. Perfecto para briefings y reels.",
      f10Detail: "Convierte instantáneamente cualquier diagrama de jugada en un video animado. Úsalo para briefings previos al juego, contenido en redes sociales o para enviar jugadas directamente a jugadores.",
      f10b1: "Auto-anima cualquier diagrama",
      f10b2: "Exporta como video MP4",
      f10b3: "Usa para reels en redes sociales",
      f10b4: "Envía clips a jugadores",

      // Testimonials
      testimonialLabel: "Testimonio de entrenadores",
      coachesLoveIt: "LOS ENTRENADORES",
      coachesLoveItAccent: "LO AMAN.",
      t1Quote: "HoopsAtlas cambió completamente cómo preparo a mi equipo. Construir jugadas solía tomar horas — ahora toma minutos.",
      t1Role: "Entrenador Principal Sub-18",
      t2Quote: "El constructor de torneos ya vale la pena. Organicé un evento de 16 equipos y todo se organizó solo automáticamente.",
      t2Role: "Director del Club",
      t3Quote: "Mis jugadores realmente entienden el cuaderno ahora. Los diagramas hablan más fuerte que cualquier sesión de pizarra.",
      t3Role: "Entrenador Asistente",

      // Blog
      coachingBlog: "Blog de Entrenamiento",
      insightsTitle: "PERSPECTIVAS",
      insightsAccent: "DE COACHING.",
      insightsSubtitle: "Perspectivas sobre estrategia moderna de baloncesto, entrenamiento basado en datos y el cuaderno digital.",
      blog1Tag: "Estrategia",
      blog1Date: "Marzo 2026",
      blog1Title: "La Muerte del Cuaderno en Papel",
      blog1Desc: "En la era moderna del baloncesto, la velocidad lo es todo. La transición de las pizarras físicas a las plataformas digitales representa un cambio fundamental en cómo se crea y comparte la información táctica.",
      blog2Tag: "Analítica",
      blog2Date: "Marzo 2026",
      blog2Title: "Desarrollo de Jugadores Basado en Datos",
      blog2Desc: "Los datos solo valen tanto como tu capacidad para interpretarlos. Al rastrear KPIs y métricas de crecimiento, los entrenadores obtienen información accionable antes reservada para staffs de élite.",
      blog3Tag: "Cultura",
      blog3Date: "Marzo 2026",
      blog3Title: "Construyendo Cultura de Club en la Era Digital",
      blog3Desc: "Un club es más que una colección de jugadores — es una identidad táctica compartida. En la era digital, mantener esa identidad en múltiples categorías de edad requiere una fuente de verdad centralizada.",
      readMore: "Leer más",
      viewAllPosts: "Ver todas las publicaciones",

      // Pricing
      pricingLabel: "Precios",
      pricingTitle: "ELIGE TU",
      pricingAccent: "PLAN.",
      pricingSubtitle: "Comienza gratis. Mejora cuando estés listo para llevar tu entrenamiento al siguiente nivel.",
      freeTier: "Gratis",
      noCreditCard: "Sin tarjeta de crédito",
      billedMonthly: "Facturación mensual, cancela cuando quieras",
      customPricing: "Precio personalizado para clubes grandes",
      getStartedFree: "Empezar Gratis",
      getPro: "Obtener Pro",
      getUltra: "Obtener Ultra",
      getClub: "Acceso Club",
      mostPopular: "Más Popular",
      proFeature1: "Ejercicios ilimitados",
      proFeature2: "Entrenador IA",
      proFeature3: "Exportaciones HD para redes",
      proFeature4: "Creación de video",
      proFeature5: "Análisis de partidos",
      proFeature6: "Soporte prioritario",
      ultraFeature1: "8 hrs análisis IA de juego/mes",
      ultraFeature2: "Insights automáticos de equipo y jugadores",
      ultraFeature3: "Análisis ofensivo y defensivo",
      ultraFeature4: "Momentos clave y puntos de coaching",
      ultraFeature5: "Informes de partido con áreas de mejora",
      clubFeature1: "10 licencias – ilimitadas",
      clubFeature2: "Cuaderno compartido",
      clubFeature3: "Consola de administración",
      clubFeature4: "Soporte prioritario 24/7",
      clubFeature5: "Informes de marca blanca",
      freeFeature1: "1 ejercicio",
      freeFeature2: "Acceso al equipo",
      freeFeature3: "Exportación manual",
      freeFeature4: "Acceso a la comunidad",
      saveMonths: "Ahorra 2 meses",

      // CTA
      timeToWin: "HORA DE",
      timeToWinAccent: "GANAR.",
      ctaSubtitle: "Deja de dibujar en papel. Organiza tu equipo, digitaliza tu cuaderno y haz tu entrenamiento basado en datos — gratis.",
      noCreditCardRequired: "Sin tarjeta de crédito · Plan gratuito disponible siempre",

      // Footer
      footerDesc: "La plataforma de entrenamiento para organizaciones modernas de baloncesto. Est. 2026, Bélgica.",
      footerBasketball: "Baloncesto",
      footerPlatform: "Plataforma",
      footerPlays: "Jugadas de Baloncesto",
      footerOffense: "Jugadas de Ataque",
      footerDefense: "Jugadas de Defensa",
      footerInbounds: "Jugadas de Saque",
      footerBlog: "Blog de Entrenamiento",
      footerCoachBoard: "Pizarra del Entrenador",
      footerAICoach: "Entrenador IA Baloncesto",
      footerPlaybookCreator: "Creador de Cuadernos",
      footerAbout: "Sobre HoopsAtlas",
      footerPrivacy: "Política de Privacidad",
      footerPrivacyShort: "Privacidad",
      footerTerms: "Términos",
      footerCopyright: "HOOPSATLAS © 2026 · BÉLGICA",

      // App store
      getItOn: "Disponible en",
      googlePlay: "Google Play",

      // Generic
      home: "Inicio",
      library: "Biblioteca",
      training: "Entrenamiento",
      playbook: "Cuaderno",
      player: "Jugador",
      coach: "Entrenador",
      club: "Club",
      parent: "Padre/Madre",
      squad: "Equipo",
      match: "Partido",
      create: "Crear",
      back: "Volver",
      backToHome: "Volver al inicio",

      // PageLayout
      playsLibrary: "Biblioteca de Jugadas",
      plPageFooterDesc: "La plataforma de cuadernos de baloncesto con IA para entrenadores de todos los niveles.",

      // AboutPage
      aboutReturnHQ: "Volver al HQ",
      aboutProtocol: "Protocolo: Acceso Total",
      aboutBriefing: "Informe Táctico",
      aboutHeroTitle: "VISIÓN COMPLETA DEL SISTEMA.",
      aboutHeroAccent: "DEL SISTEMA.",
      aboutHeroDesc: "HoopsAtlas no es solo una app; es un ecosistema digital para la inteligencia baloncestística. Desde tácticas impulsadas por IA hasta gestión de torneos en tiempo real.",
      aboutOriginTitle: "Sobre HoopsAtlas",
      aboutOriginSub: "El origen de nuestra directiva táctica",
      aboutStory1: "HoopsAtlas no empezó como una «idea de negocio» — empezó como la solución a un problema que tenía cada semana como entrenador.",
      aboutStory2: "Soy Stan, entrenador y jugador de baloncesto. Antes de cada entrenamiento hacía siempre lo mismo: buscar ejercicios, redibujar jugadas, juntar planes de notas, fotos y distintas apps. Llevaba tiempo — y no estaba organizado.",
      aboutStory3: "Así que decidí construir la herramienta que realmente quería usar: una plataforma donde los entrenadores puedan organizar su cuaderno, ejercicios y planes de práctica — siempre accesible cuando la necesitas. Eso se convirtió en HoopsAtlas.",
      aboutStory4: "Hoy, los entrenadores usan HoopsAtlas para planificar más rápido, comunicarse con más claridad con su equipo y construir estructura durante toda la temporada — sin el caos.",
      aboutMethodLabel: "La Metodología HoopsAtlas",
      aboutMethodTitle: "REDEFINIENDO ",
      aboutMethodAccent: "EL JUEGO.",
      aboutS1Title: "01. Geometría del Espaciado",
      aboutS1P1: "El baloncesto moderno es un juego de centímetros y ángulos. Nuestra metodología se centra en los principios de espaciado «Five-Out» y «Four-Out» que dominan el juego profesional hoy. Usando IA para analizar la posición de los jugadores, ayudamos a los entrenadores a identificar los momentos exactos donde el espaciado colapsa, generando pérdidas o tiros forzados.",
      aboutS1P2: "Creemos que cada set ofensivo debe crear al menos tres amenazas de anotación simultáneas. Nuestro motor táctico está programado para reconocer estas amenazas y sugerir movimientos que maximicen el estrés defensivo.",
      aboutS2Title: "02. Entrenamiento Basado en Decisiones",
      aboutS2P1: "Los ejercicios no deben ser solo repetición; deben servir para leer el juego. HoopsAtlas promueve los «Juegos en Espacios Reducidos» (SSGs) que obligan a los jugadores a tomar decisiones tácticas rápidas bajo presión. Nuestra biblioteca de ejercicios está categorizada por las «lecturas» específicas que desarrollan: desde la cobertura del pick-and-roll hasta las rotaciones del lado débil.",
      aboutS2P2: "Al integrar estos ejercicios en un plan de temporada coherente, los entrenadores pueden asegurar que el coeficiente baloncestístico de su equipo crezca junto con sus habilidades físicas. El cuaderno digital permite compartir estos conceptos al instante, asegurando que todos estén alineados antes de pisar la cancha.",
      aboutS3Title: "03. Retroalimentación Basada en Datos",
      aboutS3P1: "El video es el árbitro definitivo. Nuestro motor Atlas Vision convierte el metraje bruto en puntos de datos accionables. Nos centramos en el «Porcentaje de Tiro Efectivo» (eFG%) y en los «Toques en Pintura» como los principales indicadores de salud ofensiva.",
      aboutS3P2: "En vez de observaciones subjetivas, los entrenadores pueden presentar a los jugadores evidencia objetiva de su rendimiento. Este enfoque basado en datos genera confianza y acelera la curva de aprendizaje de los atletas de todos los niveles.",
      aboutS4Title: "04. El Concepto de HQ Digital",
      aboutS4P1: "La mente de un entrenador suele estar llena de jugadas, calendarios y estadísticas. HoopsAtlas funciona como un «Segundo Cerebro», externalizando esta información en una sede digital segura y organizada. Esto reduce la carga cognitiva y permite a los entrenadores enfocarse en lo que mejor saben hacer: enseñar el juego.",
      aboutS4P2: "Desde el chat del «Vestuario» hasta la «Bóveda Táctica», cada función está diseñada para agilizar la parte administrativa del entrenamiento, dándote más tiempo para enfocarte en tus jugadores y tu estrategia.",
      aboutFC1Sub: "Síntesis IA",
      aboutFC1Title: "Magic Coach",
      aboutFC1Desc: "Describe tus tácticas en texto y deja que la IA genere al instante diagramas profesionales y planes paso a paso. No más horas dibujando en un portapapeles.",
      aboutFC2Sub: "Motor de Visión",
      aboutFC2Title: "Atlas Vision",
      aboutFC2Desc: "Sube grabaciones de partidos. Nuestra IA analiza el espaciado, la calidad de los tiros y las rotaciones defensivas para crear un informe táctico detallado.",
      aboutFC3Sub: "Competición",
      aboutFC3Title: "Constructor de Torneos",
      aboutFC3Desc: "Gestiona torneos completos. El motor maneja grupos, horarios para múltiples canchas y hace seguimiento de clasificaciones y marcadores en vivo.",
      aboutFC4Sub: "Contenido Social",
      aboutFC4Title: "Social Studio",
      aboutFC4Desc: "Convierte las tácticas en éxito viral. Exporta gráficos y videos profesionales de tus jugadas, optimizados para TikTok e Instagram Reels.",
      aboutFC5Sub: "Movimiento",
      aboutFC5Title: "Motor de Movimiento",
      aboutFC5Desc: "Convierte diagramas estáticos en animaciones de video en HD. Los jugadores ven exactamente cómo fluyen las líneas de movimiento y las acciones.",
      aboutFC6Sub: "Bóveda en la Nube",
      aboutFC6Title: "Cuaderno Digital",
      aboutFC6Desc: "Tu archivo táctico completo en la nube. Crea cuadernos para equipos específicos y sincronízalos al instante con todos los entrenadores y jugadores.",
      aboutFC7Sub: "Organización",
      aboutFC7Title: "Sede del Club",
      aboutFC7Desc: "Para clubes: gestiona todo tu personal. Patrocina entrenadores para funciones Pro y comparte sistemas tácticos centrales en toda tu organización.",
      aboutFC8Sub: "Logística",
      aboutFC8Title: "Hub del Equipo",
      aboutFC8Desc: "Gestiona tus plantillas, controla la asistencia a entrenamientos, planifica eventos y comunícate a través del chat seguro del vestuario.",
      aboutFC9Sub: "Navegación",
      aboutFC9Title: "Terrenos Tácticos",
      aboutFC9Desc: "Encuentra todas las canchas de baloncesto cercanas. Usa IA y Mapas para descubrir tanto canchas exteriores públicas como pabellones deportivos interiores.",
      aboutTechTitle: "Hardware e Integración",
      aboutTechTitleAccent: "API",
      aboutTechSub: "Construido sobre infraestructura élite",
      aboutTechCore: "Razonamiento Táctico Principal",
      aboutTechGateway: "Pasarela Segura de Licencias",
      aboutTechStorage: "Almacenamiento de Grado Militar",
      aboutTechGeo: "Geolocalización Sectorial",
      aboutCTATitle: "EMPIEZA A LIDERAR.",
      aboutCTADesc: "Deja de dibujar en papel. Activa tu sede digital y domina la competencia con datos e IA.",
      aboutCTABtn: "Inicializar el HQ",
      aboutModuleActive: "Módulo Activo",

      // BasketballPlaysPage
      bpFreeLibrary: "Biblioteca Gratuita de Jugadas",
      bpHeroTitle: "Jugadas de ",
      bpHeroAccent: "Baloncesto",
      bpHeroTitle2: "",
      bpHeroDesc: "+ jugadas de baloncesto gratuitas para entrenadores de todos los niveles — sets ofensivos, esquemas defensivos y jugadas de saque. Dibuja cualquier jugada en la pizarra de HoopsAtlas en segundos.",
      bpDrawFree: "Dibuja una Jugada Gratis",
      bpBuildPlaybook: "Crea tu Cuaderno",
      bpOffenseLabel: "Jugadas de Ataque",
      bpOffenseDesc: "Sets de media cancha, ofensivas de movimiento, acciones de pick-and-roll y jugadas de transición.",
      bpDefenseLabel: "Jugadas de Defensa",
      bpDefenseDesc: "Sistemas hombre a hombre, zona, presión y sistemas híbridos para detener cualquier ataque.",
      bpInboundsLabel: "Jugadas de Saque",
      bpInboundsDesc: "Sets BLOB y SLOB diseñados para anotar desde situaciones de saque de banda.",
      bpAllPlays: "Todas las ",
      bpAllPlaysAccent: "Jugadas",
      bpCTATitle: "Dibuja Cualquier Jugada",
      bpCTAAccent: "En Segundos.",
      bpCTADesc: "Usa la pizarra de HoopsAtlas para diagramar cualquier jugada, crear tu cuaderno y compartirlo con tu equipo — todo en un solo lugar.",
      bpStartFree: "Empezar Gratis",
      bpPlays: "jugadas",

      // BlogPage
      blogCoachingBlog: "Blog de Entrenamiento",
      blogHeroTitle: "Perspectivas de ",
      blogHeroAccent: "Entrenamiento",
      blogHeroTitle2: " de Baloncesto",
      blogHeroDesc: "Guías, análisis de jugadas y consejos de entrenamiento para entrenadores de baloncesto de todos los niveles — desde juveniles hasta semiprofesionales.",
      blogReadArticle: "Leer Artículo",
      blogRead: "Leer",
      blogCTATitle: "¿Listo para probar HoopsAtlas?",
      blogCTADesc: "Dibuja jugadas, crea tu cuaderno y genera ejercicios con IA — gratis para cualquier entrenador.",
      blogStartFree: "Empezar Gratis",

      // CoachBoardPage
      cbDigitalCoach: "Pizarra Digital del Entrenador",
      cbHeroTitle: "La Pizarra Digital",
      cbHeroAccent: "del Entrenador",
      cbHeroTitle2: "de Baloncesto",
      cbHeroDesc: "Dibuja jugadas de baloncesto, diagrama esquemas defensivos y crea tu cuaderno en una cancha digital interactiva — más rápido y claro que cualquier pizarra o diagrama en papel.",
      cbOpenFree: "Abrir Pizarra Digital Gratis",
      cbNoDownload: "Sin descarga · Funciona en cualquier dispositivo · Plan gratuito disponible",
      cbFeaturesTitle: "Todo en una sola ",
      cbFeaturesAccent: "pizarra.",
      cbF1Title: "Cancha Interactiva Completa",
      cbF1Desc: "Vistas de media cancha y cancha completa con todas las marcas estándar. Dibuja en una cancha de baloncesto real, no en un lienzo en blanco.",
      cbF2Title: "Herramientas de Dibujo Inteligentes",
      cbF2Desc: "Pases, botes, cortes, bloqueos y rutas de jugadores — todos los símbolos estándar de baloncesto, disponibles con un toque.",
      cbF3Title: "Jugadores Numerados",
      cbF3Desc: "Coloca jugadores ofensivos y defensivos por posición (1–5). Agrega nombres para asignaciones personalizadas.",
      cbF4Title: "Guarda en tu Cuaderno",
      cbF4Desc: "Cada jugada que dibujas se guarda directamente en tu cuaderno digital, organizado y accesible desde cualquier dispositivo.",
      cbF5Title: "Comparte al Instante",
      cbF5Desc: "Exporta el diagrama de tu jugada como imagen y compártelo con tu equipo por WhatsApp, email o en la app.",
      cbF6Title: "Generador de Jugadas IA",
      cbF6Desc: "Describe una jugada en texto simple y la IA genera el diagrama por ti — en segundos.",
      cbWhyTitle: "Por qué los entrenadores cambian a una ",
      cbWhyAccent: "pizarra digital",
      cbWhy1Title: "El papel se pierde",
      cbWhy1Desc: "Una jugada digital permanece en tu cuaderno para siempre. Busca, edita y comparte en segundos.",
      cbWhy2Title: "Las pizarras no se pueden compartir",
      cbWhy2Desc: "Cada diagrama que dibujas en HoopsAtlas puede compartirse con todo tu equipo al instante.",
      cbWhy3Title: "Los diagramas estáticos pierden contexto",
      cbWhy3Desc: "Agrega notas de entrenamiento, roles de jugadores y opciones de lectura directamente junto a cada jugada.",
      cbWhy4Title: "Tu cuaderno vive en la app",
      cbWhy4Desc: "Cada jugada que dibujas se guarda, organiza y es accesible en cualquier dispositivo.",
      cbBoardDesc: "Media cancha interactiva · Dibuja jugadas en segundos · Comparte con un toque",
      cbTryFree: "Pruébalo Gratis",
      cbRelated: "Funciones Relacionadas",
      cbRelated1Label: "Entrenador IA de Baloncesto",
      cbRelated1Desc: "Genera ejercicios y jugadas con IA en segundos.",
      cbRelated2Label: "Creador de Cuadernos",
      cbRelated2Desc: "Organiza todas tus jugadas en un cuaderno digital completo.",
      cbRelated3Label: "Biblioteca Gratuita de Jugadas",
      cbRelated3Desc: "35+ jugadas de baloncesto listas para dibujar y usar.",

      // AICoachPage
      aiFeature: "Función IA",
      aiHeroTitle: "Entrenador IA",
      aiHeroAccent: "de Baloncesto",
      aiHeroTitle2: "y Generador de Ejercicios",
      aiHeroDesc: "Describe lo que necesitas en texto simple y la IA de HoopsAtlas genera un diagrama completo de jugada o ejercicio — en segundos. Sin necesidad de dibujar.",
      aiTryFree: "Probar Entrenador IA Gratis",
      aiHowTitle: "Cómo ",
      aiHowAccent: "funciona.",
      aiStep1Title: "Describe tu Jugada o Ejercicio",
      aiStep1Desc: "Escribe lo que quieres en español simple — «un pick and roll desde arriba con un tirador en la esquina» o «un ejercicio de cierre defensivo para principiantes».",
      aiStep2Title: "La IA Genera el Contenido",
      aiStep2Desc: "La IA entiende las tácticas de baloncesto y crea un diagrama completo o un ejercicio paso a paso con indicaciones de entrenamiento.",
      aiStep3Title: "Edita, Guarda y Comparte",
      aiStep3Desc: "Ajusta el resultado en la pizarra del entrenador, guárdalo en tu cuaderno y compártelo con tu equipo al instante.",
      aiWhatTitle: "Qué puedes ",
      aiWhatAccent: "generar.",
      aiYouType: "Tú escribes:",
      aiGenerates: "La IA genera:",
      aiEx1Prompt: "«Una presión a cancha completa con trampa en media cancha»",
      aiEx1Result: "Genera un diagrama completo de presión 2-2-1 con zonas de trampa y flechas de rotación.",
      aiEx2Prompt: "«Pick and roll desde arriba con tirador en la esquina»",
      aiEx2Result: "Produce un diagrama de PnR con el rollo, el espaciado en la esquina y las opciones de lectura etiquetadas.",
      aiEx3Prompt: "«Un ejercicio para mejorar el trabajo de pies defensivo para jugadores Sub-14»",
      aiEx3Result: "Devuelve un ejercicio paso a paso con tiempos, instrucciones de montaje e indicaciones de entrenamiento.",
      aiBuiltTitle: "Para todo tipo de ",
      aiBuiltAccent: "entrenador.",
      aiUC1Role: "Entrenadores Juveniles",
      aiUC1Desc: "Genera ejercicios apropiados para cada edad al instante. Sin necesidad de buscar en YouTube 30 minutos — describe qué habilidad quieres trabajar y obtén un ejercicio listo para usar.",
      aiUC2Role: "Entrenadores de Club",
      aiUC2Desc: "Construye un sistema táctico consistente en múltiples categorías de edad. Usa IA para generar jugadas que se adapten a tu sistema y ajústalas para diferentes categorías.",
      aiUC3Role: "Entrenadores Escolares",
      aiUC3Desc: "Prepara sesiones de entrenamiento en minutos. Deja que la IA se encargue del diseño de ejercicios mientras tú te enfocas en el entrenamiento.",
      aiUC4Role: "Entrenadores Pro y Semipro",
      aiUC4Desc: "Usa IA para redactar conceptos iniciales de jugadas, luego refínalos en la pizarra. Preparación más rápida, más tiempo para el análisis.",
      aiCTATitle: "Tu Entrenador IA",
      aiCTAAccent: "de Baloncesto",
      aiCTATitle2: "está listo.",
      aiCTADesc: "Genera jugadas, ejercicios y planes de práctica en segundos. Gratis para probar — sin tarjeta de crédito.",
      aiStartFree: "Empezar Gratis",
      aiLink1Label: "Pizarra del Entrenador",
      aiLink1Desc: "Dibuja y edita cualquier jugada en la cancha interactiva.",
      aiLink2Label: "Creador de Cuadernos",
      aiLink2Desc: "Organiza todas tus jugadas en un cuaderno digital.",
      aiLink3Label: "Biblioteca Gratuita de Jugadas",
      aiLink3Desc: "Explora más de 35 jugadas de baloncesto listas para usar.",

      // PlaybookCreatorPage
      pbDigital: "Cuaderno Digital",
      pbHeroTitle: "Creador de",
      pbHeroAccent: "Cuadernos",
      pbHeroTitle2: "de Baloncesto",
      pbHeroDesc: "Crea un cuaderno de baloncesto digital completo — dibuja tus jugadas, organízalas por categoría y comparte el cuaderno completo con tu equipo con un toque.",
      pbCreateFree: "Crear tu Cuaderno Gratis",
      pbFreePlan: "Plan gratuito disponible · Sin descarga · Funciona en iOS, Android y web",
      pbWholeTitle: "Todo tu sistema",
      pbWholeAccent: "en un solo lugar.",
      pbF1Title: "Organiza por Categoría",
      pbF1Desc: "Clasifica jugadas en ataque, defensa, BLOB, SLOB y fin de partido. Encuentra cualquier jugada en segundos.",
      pbF2Title: "Dibuja en la Pizarra",
      pbF2Desc: "Cada jugada se dibuja en una media cancha interactiva con símbolos estándar de baloncesto.",
      pbF3Title: "Creación Asistida por IA",
      pbF3Desc: "Describe una jugada en texto y la IA genera el diagrama. Refínalo en la pizarra.",
      pbF4Title: "Asigna a Jugadores",
      pbF4Desc: "Etiqueta jugadas para jugadores o posiciones específicas para que todos conozcan su rol.",
      pbF5Title: "Comparte con tu Equipo",
      pbF5Desc: "Envía el cuaderno completo o jugadas individuales a los jugadores por la app o como exportaciones.",
      pbF6Title: "Privado y Seguro",
      pbF6Desc: "Tu cuaderno es privado por defecto. Comparte solo lo que quieras, con quien quieras.",
      pbStepsTitle: "Crea tu cuaderno",
      pbStepsAccent: "en 4 pasos.",
      pbStep1Title: "Define tu sistema",
      pbStep1Desc: "Elige tu base ofensiva y defensiva. Tu cuaderno debe reflejar una identidad táctica clara.",
      pbStep2Title: "Dibuja tus 10 jugadas principales",
      pbStep2Desc: "Empieza con las jugadas que corres cada partido: 2-3 sets de media cancha, 1 BLOB, 1 SLOB, 1 jugada rápida. Domina esas antes de agregar más.",
      pbStep3Title: "Agrega notas de entrenamiento a cada jugada",
      pbStep3Desc: "Cada jugada tiene 3-5 puntos de entrenamiento que explican las lecturas clave y los puntos de decisión.",
      pbStep4Title: "Comparte con tu equipo",
      pbStep4Desc: "Envía el cuaderno completo a tu plantilla. Los jugadores pueden revisar jugadas antes del entrenamiento y acceder a ellas durante los partidos.",
      pbLearnMore: "Saber más",
      pbLink1Label: "Cómo Crear un Cuaderno",
      pbLink1Desc: "Guía paso a paso para crear tu primer cuaderno.",
      pbLink2Label: "Pizarra del Entrenador",
      pbLink2Desc: "Dibuja jugadas en la cancha digital interactiva.",
      pbLink3Label: "Biblioteca Gratuita de Jugadas",
      pbLink3Desc: "Explora más de 35 jugadas de baloncesto para agregar a tu cuaderno.",
    },
    nl: {
      // Nav
      features: "Functies",
      howItWorks: "Hoe het werkt",
      blog: "Blog",
      pricing: "Prijzen",
      becomePartner: "Word Partner",
      startNow: "Begin nu",
      dashboard: "Dashboard",

      // Hero
      nowLive: "Nu live — v2.5",
      heroTitle1: "HET AI",
      heroTitle2: "BASKETBAL",
      heroTitle3: "PLAYBOOK",
      heroTitle4: "& COACHBORD",
      heroSubtitle: "Het alles-in-één coachingplatform voor modern basketbal. Maak oefeningen, beheer je team en analyseer wedstrijden met AI.",
      startFree: "Begin gratis",
      lovedBy: "Geliefd door",
      coaches: "coaches",

      // Stats
      statCoaches: "Coaches",
      statDrills: "Oefeningen gemaakt",
      statCountries: "Landen",
      statRating: "App-beoordeling",

      // Pillars
      pillar1Title: "Coach Hub",
      pillar1Desc: "Maak professionele diagrammen en oefeningen met AI. Niet meer tekenen op papier — genereer spelen in seconden.",
      pillar2Title: "Teambeheer",
      pillar2Desc: "Aanwezigheidsregistratie, teamberichten en tactische opdrachten — alles op één plek voor je volledige selectie.",
      pillar3Title: "Toernooi-engine",
      pillar3Desc: "Beheer brackets, live scores en veldverdeling voor evenementen van elk formaat, van lokaal tot nationaal.",

      // How it works
      simpleByDesign: "Eenvoudig van opzet",
      howItWorksTitle: "HOE HET",
      howItWorksTitleAccent: "WERKT.",
      step1Title: "Bouw je Playbook",
      step1Desc: "Gebruik AI of slepen-en-neerzetten om professionele diagrammen te maken voor elk spel, elke oefening of vrij schot in seconden.",
      step2Title: "Organiseer je Team",
      step2Desc: "Voeg spelers toe, volg aanwezigheid, wijs oefeningen toe en stuur tactische briefings rechtstreeks naar je team.",
      step3Title: "Analyseer & Verbeter",
      step3Desc: "Bekijk wedstrijdbeelden, volg spelersstatistieken en ontvang AI-gestuurde coachinginzichten na elke wedstrijd.",

      // Features section
      everythingYouNeed: "Alles wat je nodig hebt",
      allTheToolsTitle: "ALLE",
      allTheToolsAccent: "TOOLS.",
      allTheToolsSubtitle: "Één platform, van begin tot eind gebouwd voor basketbalcoaches op elk niveau.",
      openInApp: "Openen in app →",
      learnMore: "Meer weten →",

      // Features
      f1Title: "AI Oefening Maker",
      f1Desc: "Beschrijf een oefening in gewone tekst en krijg een professioneel diagram in seconden. Geen ontwerpvaardigheden nodig.",
      f1Detail: "Aangedreven door AI zet de Oefening Maker gewone taal om in volledige, professionele oefendiagrammen. Typ gewoon wat je wilt — de AI regelt automatisch de afstanden, spelersposities, pijlen en notities.",
      f1b1: "Genereer oefeningen vanuit tekst in seconden",
      f1b2: "Volledige velddiagrammen met pijlen & spelers",
      f1b3: "Sla op in je persoonlijke oefenbibliotheek",
      f1b4: "Deel direct met je team",

      f2Title: "Toernooi Maker",
      f2Desc: "Beheer brackets, live scores en veldverdeling voor elk evenement — van 4 tot 64 teams.",
      f2Detail: "Organiseer toernooien van elke omvang zonder spreadsheets. De Toernooi Maker regelt automatisch bracketgeneratie, live scores en veldplanning.",
      f2b1: "Automatische bracketgeneratie",
      f2b2: "Live score-updates",
      f2b3: "Veldplanning & -verdeling",
      f2b4: "Deelbare publieke bracketlink",

      f3Title: "Playbook Kluis",
      f3Desc: "Gecentraliseerde opslag voor je volledige playbook, toegankelijk vanaf elk apparaat, altijd.",
      f3Detail: "Al je spelen, oefeningen en sets op één plek. Organiseer op categorie, label op niveau en deel specifieke secties met spelers of assistenten.",
      f3b1: "Onbeperkte playbookopslag",
      f3b2: "Organiseer op categorie & tags",
      f3b3: "Deel met spelers of staf",
      f3b4: "Toegankelijk vanaf elk apparaat",

      f4Title: "Team Hub",
      f4Desc: "Aanwezigheidsregistratie, directe berichten en oefenopdrachten op één plek voor je volledige team.",
      f4Detail: "Beheer je volledige selectie zonder van app te wisselen. Volg aanwezigheid, stuur berichten, wijs oefeningen toe en monitor de voortgang van spelers — alles vanuit één dashboard.",
      f4b1: "Selectie- & aanwezigheidsregistratie",
      f4b2: "Directe teamberichten",
      f4b3: "Oefenopdrachten per speler",
      f4b4: "Trainingsgeschiedenis",

      f5Title: "Wedstrijdanalyse",
      f5Desc: "Upload beeldmateriaal en ontvang AI-gestuurde analyses van prestaties, afstanden en defensieve patronen.",
      f5Detail: "Upload wedstrijdbeelden en laat AI afstanden, rotaties en defensieve patronen analyseren. Markeer sleutelmomenten en deel clips direct met spelers.",
      f5b1: "AI-gestuurde beeldanalyse",
      f5b2: "Afstands- & rotatieanalyses",
      f5b3: "Clip-creatie & -deling",
      f5b4: "Herkenning van defensieve patronen",

      f6Title: "Scrimmage Hub",
      f6Desc: "Plaatsen en vinden van scrimmageverzoeken in jouw omgeving. Organiseer oefenwedstrijden met een paar tikken.",
      f6Detail: "Vind tegenstanders bij jou in de buurt voor scrimmages en oefenwedstrijden. Publiceer beschikbaarheid, bekijk open verzoeken en bevestig wedstrijden direct in de app.",
      f6b1: "Plaatsen & vinden van scrimmageverzoeken",
      f6b2: "Filteren op locatie & niveau",
      f6b3: "Wedstrijden bevestigen en inplannen",
      f6b4: "Teamcommunicatie in de app",

      f7Title: "Coach Hub",
      f7Desc: "Verbind met andere coaches, deel kennis en volg berichten uit de coachingsgemeenschap.",
      f7Detail: "Een coachingsgemeenschap ingebouwd in het platform. Deel spelen, stel vragen, post inzichten en volg topcaches — zonder HoopsAtlas te verlaten.",
      f7b1: "Community-feed voor coaches",
      f7b2: "Deel spelen & diagrammen",
      f7b3: "Volg coaches & clubs",
      f7b4: "Ontdek coachingscontent",

      f8Title: "Club HQ",
      f8Desc: "Gedeelde playbooks, multi-team beheer en een beheerconsole voor je hele club onder één dak.",
      f8Detail: "Gebouwd voor clubs met meerdere teams. Club HQ geeft beheerders een centrale console om coaches te beheren, playbooks te delen en alle activiteit te overzien.",
      f8b1: "Multi-team beheer",
      f8b2: "Gedeeld clubplaybook",
      f8b3: "Beheerconsole & rechten",
      f8b4: "Clubbrede oefenbibliotheek",

      f9Title: "Wedstrijdarchief",
      f9Desc: "Sla je wedstrijdopnames op, label en bekijk ze opnieuw. Bouw een volledige videobibliotheek van je seizoen.",
      f9Detail: "Elke wedstrijd opgeslagen, gelabeld en doorzoekbaar. Bouw een compleet videoarchief van je seizoen en herbekijk sleutelmomenten wanneer je wilt.",
      f9b1: "Seizoenslange videobibliotheek",
      f9b2: "Label & tag opnames",
      f9b3: "Doorzoekbaar archief",
      f9b4: "Deel clips met staf",

      f10Title: "Video Generator",
      f10Desc: "Zet je tactische diagrammen automatisch om in geanimeerde videoclips. Perfect voor spelersbriefings en reels.",
      f10Detail: "Converteer elk speldiagram onmiddellijk naar een geanimeerde video. Gebruik het voor pre-wedstrijdbriefings, social media-content of stuur spelen direct naar spelers.",
      f10b1: "Animeer automatisch elk diagram",
      f10b2: "Exporteer als MP4-video",
      f10b3: "Gebruik voor social media reels",
      f10b4: "Stuur clips naar spelers",

      // Testimonials
      testimonialLabel: "Coach-getuigenissen",
      coachesLoveIt: "COACHES",
      coachesLoveItAccent: "HOUDEN ERVAN.",
      t1Quote: "HoopsAtlas veranderde volledig hoe ik mijn team voorberei. Spelen bouwen duurde vroeger uren — nu duurt het minuten.",
      t1Role: "U18 Hoofdcoach",
      t2Quote: "De toernooi maker alleen al is de moeite waard. Ik organiseerde een evenement met 16 teams en alles regelde zich automatisch.",
      t2Role: "Clubdirecteur",
      t3Quote: "Mijn spelers begrijpen het playbook nu echt. De diagrammen spreken luider dan welke whiteboardsessie dan ook.",
      t3Role: "Assistent Coach",

      // Blog
      coachingBlog: "Coachingsblog",
      insightsTitle: "COACHING",
      insightsAccent: "INZICHTEN.",
      insightsSubtitle: "Perspectieven over moderne basketbalstrategie, datagestuurde coaching en het digitale playbook.",
      blog1Tag: "Strategie",
      blog1Date: "Maart 2026",
      blog1Title: "De Dood van het Papieren Playbook",
      blog1Desc: "In het moderne basketbaltijdperk is snelheid alles. De overgang van fysieke clipboards naar digitale platforms vertegenwoordigt een fundamentele verschuiving in hoe tactische informatie wordt gecreëerd en gedeeld.",
      blog2Tag: "Analytics",
      blog2Date: "Maart 2026",
      blog2Title: "Datagestuurde Spelersontwikkeling",
      blog2Desc: "Data is alleen zo goed als je vermogen om het te interpreteren. Door KPI's en groeimetriek bij te houden, krijgen coaches bruikbare inzichten die vroeger voorbehouden waren aan elitestaven.",
      blog3Tag: "Cultuur",
      blog3Date: "Maart 2026",
      blog3Title: "Clubcultuur Opbouwen in het Digitale Tijdperk",
      blog3Desc: "Een club is meer dan een verzameling spelers — het is een gedeelde tactische identiteit. In het digitale tijdperk vereist het handhaven van die identiteit over meerdere leeftijdsgroepen een gecentraliseerde informatiebron.",
      readMore: "Lees meer",
      viewAllPosts: "Bekijk alle berichten",

      // Pricing
      pricingLabel: "Prijzen",
      pricingTitle: "KIES JE",
      pricingAccent: "ABONNEMENT.",
      pricingSubtitle: "Begin gratis. Upgrade wanneer je klaar bent om je coaching naar een hoger niveau te tillen.",
      freeTier: "Gratis",
      noCreditCard: "Geen creditcard vereist",
      billedMonthly: "Maandelijks gefactureerd, op elk moment opzegbaar",
      customPricing: "Aangepaste prijzen voor grote clubs",
      getStartedFree: "Begin Gratis",
      getPro: "Kies Pro",
      getUltra: "Kies Ultra",
      getClub: "Club Toegang",
      mostPopular: "Meest Populair",
      proFeature1: "Onbeperkte oefeningen",
      proFeature2: "AI Coach",
      proFeature3: "HD social exports",
      proFeature4: "Video-aanmaak",
      proFeature5: "Wedstrijdanalyse",
      proFeature6: "Prioriteitsondersteuning",
      ultraFeature1: "8 uur AI-wedstrijdanalyse/maand",
      ultraFeature2: "Automatische team- & spelersinzichten",
      ultraFeature3: "Offensieve & defensieve analyse",
      ultraFeature4: "Sleutelmomenten & coachingspunten",
      ultraFeature5: "Wedstrijdrapporten met verbeterpunten",
      clubFeature1: "10–onbeperkte licenties",
      clubFeature2: "Gedeeld playbook",
      clubFeature3: "Beheerconsole",
      clubFeature4: "24/7 prioriteitsondersteuning",
      clubFeature5: "White-label rapporten",
      freeFeature1: "1 oefening",
      freeFeature2: "Teamtoegang",
      freeFeature3: "Handmatige export",
      freeFeature4: "Community-toegang",
      saveMonths: "Bespaar 2 maanden",

      // CTA
      timeToWin: "TIJD OM TE",
      timeToWinAccent: "WINNEN.",
      ctaSubtitle: "Stop met tekenen op papier. Organiseer je team, digitaliseer je playbook en maak je coaching datagestuurd — gratis.",
      noCreditCardRequired: "Geen creditcard vereist · Gratis plan altijd beschikbaar",

      // Footer
      footerDesc: "Het coachingplatform voor moderne basketbalorganisaties. Est. 2026, België.",
      footerBasketball: "Basketbal",
      footerPlatform: "Platform",
      footerPlays: "Basketbalspelen",
      footerOffense: "Aanvalsspelen",
      footerDefense: "Verdedigingsspelen",
      footerInbounds: "Ingooi Spelen",
      footerBlog: "Coachingsblog",
      footerCoachBoard: "Coachbord",
      footerAICoach: "AI Basketbalcoach",
      footerPlaybookCreator: "Playbook Maker",
      footerAbout: "Over HoopsAtlas",
      footerPrivacy: "Privacybeleid",
      footerPrivacyShort: "Privacy",
      footerTerms: "Voorwaarden",
      footerCopyright: "HOOPSATLAS © 2026 · BELGIË",

      // App store
      getItOn: "Beschikbaar op",
      googlePlay: "Google Play",

      // Generic
      home: "Home",
      library: "Bibliotheek",
      training: "Training",
      playbook: "Playbook",
      player: "Speler",
      coach: "Coach",
      club: "Club",
      parent: "Ouder",
      squad: "Team",
      match: "Wedstrijd",
      create: "Aanmaken",
      back: "Terug",
      backToHome: "Terug naar Home",

      // PageLayout
      playsLibrary: "Spelenbibliotheek",
      plPageFooterDesc: "Het AI-basketbalplaybook en coachingplatform voor coaches op elk niveau.",

      // AboutPage
      aboutReturnHQ: "Terug naar HQ",
      aboutProtocol: "Protocol: Volledige Toegang",
      aboutBriefing: "Tactische Briefing",
      aboutHeroTitle: "VOLLEDIG SYSTEEMOVERZICHT.",
      aboutHeroAccent: "OVERZICHT.",
      aboutHeroDesc: "HoopsAtlas is niet zomaar een app; het is een digitaal ecosysteem voor basketbalintelligentie. Van AI-gestuurde tactieken tot real-time toernooibeheer.",
      aboutOriginTitle: "Over HoopsAtlas",
      aboutOriginSub: "De oorsprong van onze tactische missie",
      aboutStory1: "HoopsAtlas begon niet als een 'zakelijk idee' — het begon als de oplossing voor een probleem dat ik elke week had als coach.",
      aboutStory2: "Ik ben Stan, een basketbalcoach en speler. Voor elke training deed ik steeds dezelfde dingen: oefeningen zoeken, spelen hertekenen, plannen samenstellen uit notities, foto's en verschillende apps. Het kostte tijd — en was niet georganiseerd.",
      aboutStory3: "Dus besloot ik het hulpmiddel te bouwen dat ik zelf wilde gebruiken: één platform waar coaches hun playbook, oefeningen en trainingsplannen kunnen organiseren — altijd bereikbaar wanneer je het nodig hebt. Dat werd HoopsAtlas.",
      aboutStory4: "Vandaag gebruiken coaches HoopsAtlas om sneller te plannen, duidelijker te communiceren met hun team en structuur op te bouwen doorheen het seizoen — zonder de chaos.",
      aboutMethodLabel: "De HoopsAtlas Methodologie",
      aboutMethodTitle: "HET SPEL ",
      aboutMethodAccent: "HERDEFINIËREN.",
      aboutS1Title: "01. Ruimtegeometrie",
      aboutS1P1: "Modern basketbal is een spel van centimeters en hoeken. Onze methodologie richt zich op de 'Five-Out'- en 'Four-Out'-ruimteprincipes die vandaag het professionele spel domineren. Door AI in te zetten om spelersposities te analyseren, helpen we coaches de exacte momenten te identificeren waarop de ruimte instort, wat leidt tot balverliezen of geforceerde schoten.",
      aboutS1P2: "We geloven dat elke offensieve set tegelijkertijd minstens drie afzonderlijke scoringsdreigingen moet creëren. Onze tactische engine is geprogrammeerd om deze dreigingen te herkennen en bewegingen voor te stellen die de defensieve druk maximaliseren.",
      aboutS2Title: "02. Beslissingsgebaseerde Training",
      aboutS2P1: "Oefeningen mogen niet alleen over herhaling gaan; ze moeten gaan over het lezen van het spel. HoopsAtlas bevordert 'Small Sided Games' (SSGs) die spelers dwingen snel tactische beslissingen te nemen onder druk. Onze oefenbibliotheek is gecategoriseerd op de specifieke 'reads' die ze ontwikkelen — van pick-and-roll-dekking tot zwakke-kant-rotaties.",
      aboutS2P2: "Door deze oefeningen te integreren in een coherent seizoensplan, kunnen coaches ervoor zorgen dat het basketbal-IQ van hun team groeit naast hun fysieke vaardigheden. Het digitale playbook maakt onmiddellijk delen van deze concepten mogelijk, zodat iedereen op één lijn zit voordat ze het veld betreden.",
      aboutS3Title: "03. Datagestuurde Feedback",
      aboutS3P1: "Video is de ultieme waarheidsteller. Onze Atlas Vision-engine vertaalt ruwe beelden in bruikbare datapunten. We richten ons op 'Effective Field Goal Percentage' (eFG%) en 'Paint Touches' als de belangrijkste indicatoren van offensieve gezondheid.",
      aboutS3P2: "In plaats van subjectieve observaties kunnen coaches spelers nu objectief bewijs van hun prestaties presenteren. Deze datagestuurde aanpak bouwt vertrouwen op en versnelt de leercurve voor atleten op alle niveaus.",
      aboutS4Title: "04. Het Digitale HQ-concept",
      aboutS4P1: "Het hoofd van een coach zit vaak vol spelen, schema's en spelersstatistieken. HoopsAtlas fungeert als een 'Second Brain' en externaliseert deze informatie naar een beveiligd, georganiseerd digitaal hoofdkwartier. Dit vermindert de cognitieve belasting en stelt coaches in staat zich te concentreren op wat ze het beste doen: het spel onderwijzen.",
      aboutS4P2: "Van de 'Kleedkamer'-chat tot de 'Tactische Kluis' — elke functie is ontworpen om de administratieve kant van coaching te stroomlijnen, zodat je meer tijd hebt om je te focussen op je spelers en je strategie.",
      aboutFC1Sub: "AI Synthese",
      aboutFC1Title: "Magic Coach",
      aboutFC1Desc: "Beschrijf je tactieken in tekst en laat AI onmiddellijk professionele diagrammen en stapsgewijze plannen genereren. Niet meer uren tekenen op een klembord.",
      aboutFC2Sub: "Vision Engine",
      aboutFC2Title: "Atlas Vision",
      aboutFC2Desc: "Upload wedstrijdbeelden. Onze AI analyseert afstanden, schootkwaliteit en defensieve rotaties om een diepgaand tactisch rapport te maken.",
      aboutFC3Sub: "Competitie",
      aboutFC3Title: "Toernooi Maker",
      aboutFC3Desc: "Beheer complete toernooien. De engine regelt poules, schema's voor meerdere velden en houdt live standen en scores bij.",
      aboutFC4Sub: "Social Content",
      aboutFC4Title: "Social Studio",
      aboutFC4Desc: "Vertaal tactieken naar viraal succes. Exporteer professionele afbeeldingen en video's van je spelen, geoptimaliseerd voor TikTok en Instagram Reels.",
      aboutFC5Sub: "Beweging",
      aboutFC5Title: "Motion Engine",
      aboutFC5Desc: "Converteer statische diagrammen naar vloeiende HD-videoanimaties. Spelers zien precies hoe de bewegingslijnen en acties in elkaar overlopen.",
      aboutFC6Sub: "Cloud Kluis",
      aboutFC6Title: "Digitaal Playbook",
      aboutFC6Desc: "Je complete tactisch archief in de cloud. Maak playbooks voor specifieke teams en synchroniseer ze direct met alle coaches en spelers.",
      aboutFC7Sub: "Organisatie",
      aboutFC7Title: "Club Hoofdkwartier",
      aboutFC7Desc: "Voor clubs: beheer je volledige staf. Sponsor coaches voor Pro-functies en deel centrale tactische systemen in je hele organisatie.",
      aboutFC8Sub: "Logistiek",
      aboutFC8Title: "Team Hub",
      aboutFC8Desc: "Beheer je teamselecties, volg aanwezigheid bij trainingen, plan evenementen en communiceer via de beveiligde Kleedkamer-chat.",
      aboutFC9Sub: "Navigatie",
      aboutFC9Title: "Tactische Terreinen",
      aboutFC9Desc: "Vind elk basketbalveld in de buurt. Gebruik AI en Kaarten om zowel publieke buitenvelden als overdekte sporthallen in je sector te ontdekken.",
      aboutTechTitle: "Hardware & API",
      aboutTechTitleAccent: "Integratie",
      aboutTechSub: "Gebouwd op elite-infrastructuur",
      aboutTechCore: "Kernredenering voor Tactieken",
      aboutTechGateway: "Beveiligde Licentiegateway",
      aboutTechStorage: "Militaire Opslag",
      aboutTechGeo: "Sectorale Geo-Locatie",
      aboutCTATitle: "BEGIN TE COMMANDEREN.",
      aboutCTADesc: "Stop met tekenen op papier. Activeer je digitale hoofdkwartier en domineer de competitie met data en AI.",
      aboutCTABtn: "Initialiseer Kern HQ",
      aboutModuleActive: "Module Actief",

      // BasketballPlaysPage
      bpFreeLibrary: "Gratis Spelenbibliotheek",
      bpHeroTitle: "Basketbal ",
      bpHeroAccent: "Spelen",
      bpHeroTitle2: " Bibliotheek",
      bpHeroDesc: "+ gratis basketbalspelen voor coaches op elk niveau — offensieve sets, defensieve schema's en ingooi-spelen. Teken elk spel op het HoopsAtlas-coachbord in seconden.",
      bpDrawFree: "Teken een Spel Gratis",
      bpBuildPlaybook: "Bouw je Playbook",
      bpOffenseLabel: "Aanvalsspelen",
      bpOffenseDesc: "Halfveld-sets, bewegingsoffensieven, pick-and-roll-acties en transitiespelen.",
      bpDefenseLabel: "Verdedigingsspelen",
      bpDefenseDesc: "Man-op-man, zone, press-schema's en hybride systemen om elk offensief te stoppen.",
      bpInboundsLabel: "Ingooi Spelen",
      bpInboundsDesc: "BLOB- en SLOB-sets ontworpen om te scoren vanuit ingooisituaties.",
      bpAllPlays: "Alle ",
      bpAllPlaysAccent: "Spelen",
      bpCTATitle: "Teken Elk Spel",
      bpCTAAccent: "In Seconden.",
      bpCTADesc: "Gebruik het HoopsAtlas-coachbord om elk spel te diagrammen, je playbook te bouwen en het te delen met je team — alles op één plek.",
      bpStartFree: "Begin Gratis",
      bpPlays: "spelen",

      // BlogPage
      blogCoachingBlog: "Coachingsblog",
      blogHeroTitle: "Basketbal ",
      blogHeroAccent: "Coaching",
      blogHeroTitle2: " Inzichten",
      blogHeroDesc: "Gidsen, speelanalyses en coachingstips voor basketbalcoaches op elk niveau — van jeugd tot semi-pro.",
      blogReadArticle: "Artikel Lezen",
      blogRead: "Lezen",
      blogCTATitle: "Klaar om HoopsAtlas te proberen?",
      blogCTADesc: "Teken spelen, bouw je playbook en genereer AI-oefeningen — gratis voor elke coach.",
      blogStartFree: "Begin Gratis",

      // CoachBoardPage
      cbDigitalCoach: "Digitaal Coachbord",
      cbHeroTitle: "Het Digitale",
      cbHeroAccent: "Coachbord",
      cbHeroTitle2: "voor Basketbal",
      cbHeroDesc: "Teken basketbalspelen, diagrammeer defensieve schema's en bouw je playbook op een interactief digitaal veld — sneller en duidelijker dan welk whiteboard of papieren diagram dan ook.",
      cbOpenFree: "Open Coachbord Gratis",
      cbNoDownload: "Geen download vereist · Werkt op elk apparaat · Gratis plan beschikbaar",
      cbFeaturesTitle: "Alles op één ",
      cbFeaturesAccent: "bord.",
      cbF1Title: "Volledig Interactief Veld",
      cbF1Desc: "Half- en volledig veldweergaven met alle standaard markeringen. Teken op een echt basketbalveld, niet op een leeg canvas.",
      cbF2Title: "Slimme Tekentools",
      cbF2Desc: "Passes, dribbels, cuts, screens en spelersroutes — alle standaard basketbalsymbolen, beschikbaar met één tik.",
      cbF3Title: "Genummerde Spelers",
      cbF3Desc: "Plaats offensieve en defensieve spelers op positie (1–5). Voeg namen toe voor gepersonaliseerde spelerstoewijzingen.",
      cbF4Title: "Opslaan in je Playbook",
      cbF4Desc: "Elk spel dat je tekent wordt direct opgeslagen in je digitale playbook, georganiseerd en toegankelijk vanaf elk apparaat.",
      cbF5Title: "Direct Delen",
      cbF5Desc: "Exporteer je speldiagram als afbeelding en deel het met je team via WhatsApp, e-mail of in de app.",
      cbF6Title: "AI Spel Generator",
      cbF6Desc: "Beschrijf een spel in gewone tekst en de AI genereert het diagram voor je — in seconden.",
      cbWhyTitle: "Waarom coaches overstappen naar een ",
      cbWhyAccent: "digitaal bord",
      cbWhy1Title: "Papier raakt kwijt",
      cbWhy1Desc: "Een digitaal spel blijft permanent in je playbook. Zoeken, bewerken en delen in seconden.",
      cbWhy2Title: "Whiteboards kunnen niet gedeeld worden",
      cbWhy2Desc: "Elk diagram dat je tekent op HoopsAtlas kan direct met je volledige team gedeeld worden.",
      cbWhy3Title: "Statische diagrammen missen context",
      cbWhy3Desc: "Voeg coachingsnotities, spelersrollen en leesopties direct naast elk spel toe.",
      cbWhy4Title: "Je playbook leeft in de app",
      cbWhy4Desc: "Elk spel dat je tekent wordt opgeslagen, georganiseerd en is toegankelijk op elk apparaat.",
      cbBoardDesc: "Interactief halfveld · Teken spelen in seconden · Deel met één tik",
      cbTryFree: "Probeer Gratis",
      cbRelated: "Gerelateerde Functies",
      cbRelated1Label: "AI Basketbalcoach",
      cbRelated1Desc: "Genereer oefeningen en spelen met AI in seconden.",
      cbRelated2Label: "Playbook Maker",
      cbRelated2Desc: "Bouw en organiseer je volledige teamplaybook.",
      cbRelated3Label: "Gratis Spelenbibliotheek",
      cbRelated3Desc: "35+ gratis basketbalspelen om te tekenen en te gebruiken.",

      // AICoachPage
      aiFeature: "AI Functie",
      aiHeroTitle: "AI Basketbal",
      aiHeroAccent: "Coach",
      aiHeroTitle2: "& Oefening Generator",
      aiHeroDesc: "Beschrijf wat je nodig hebt in gewone tekst en de HoopsAtlas AI genereert een compleet basketbalspeldiagram of oefening — in seconden. Geen tekenen vereist.",
      aiTryFree: "Probeer AI Coach Gratis",
      aiHowTitle: "Hoe het ",
      aiHowAccent: "werkt.",
      aiStep1Title: "Beschrijf je Spel of Oefening",
      aiStep1Desc: "Typ wat je wilt in gewone taal — 'een pick and roll met een hoekschutter' of 'een defensieve close-out oefening voor beginners'.",
      aiStep2Title: "AI Genereert de Content",
      aiStep2Desc: "De AI begrijpt basketbaltactieken en maakt een compleet diagram of stapsgewijze oefening met coachingscues.",
      aiStep3Title: "Bewerk, Sla op en Deel",
      aiStep3Desc: "Pas de output aan op het coachbord, sla het op in je playbook en deel het direct met je team.",
      aiWhatTitle: "Wat je kunt ",
      aiWhatAccent: "genereren.",
      aiYouType: "Jij typt:",
      aiGenerates: "AI genereert:",
      aiEx1Prompt: "'Een volledig veld press met een val op halfveld'",
      aiEx1Result: "Genereert een compleet 2-2-1 press-diagram met valzones en rotatiepijlen.",
      aiEx2Prompt: "'Pick and roll vanaf de top met een hoekschutter'",
      aiEx2Result: "Produceert een PnR-diagram met de rol, de hoekruimte en de leesopties gelabeld.",
      aiEx3Prompt: "'Een oefening om defensief voetwerk te verbeteren voor U14-spelers'",
      aiEx3Result: "Geeft een stapsgewijze oefening terug met timing, opstelinsstructies en coachingscues.",
      aiBuiltTitle: "Gebouwd voor elke ",
      aiBuiltAccent: "coach.",
      aiUC1Role: "Jeugdcoaches",
      aiUC1Desc: "Genereer leeftijdsgeschikte oefeningen direct. Geen 30 minuten YouTube zoeken — beschrijf welke vaardigheid je wilt trainen en krijg een kant-en-klare oefening.",
      aiUC2Role: "Clubcoaches",
      aiUC2Desc: "Bouw een consistent tactisch systeem over meerdere leeftijdsgroepen. Gebruik AI om spelen te genereren die passen bij je systeem en pas ze aan voor verschillende leeftijdscategorieën.",
      aiUC3Role: "Schoolcoaches",
      aiUC3Desc: "Bereid trainingssessies voor in minuten. Laat AI het oefenontwerp regelen terwijl jij je focust op de coaching.",
      aiUC4Role: "Pro & Semi-Pro Coaches",
      aiUC4Desc: "Gebruik AI om initiële spelconcepten te ontwerpen, verfijn ze dan op het coachbord. Snellere voorbereiding, meer tijd voor analyse.",
      aiCTATitle: "Je AI",
      aiCTAAccent: "Basketbalcoach",
      aiCTATitle2: "is klaar.",
      aiCTADesc: "Genereer spelen, oefeningen en trainingsplannen in seconden. Gratis te proberen — geen creditcard vereist.",
      aiStartFree: "Begin Gratis",
      aiLink1Label: "Coachbord",
      aiLink1Desc: "Teken en bewerk elk spel op het interactieve veld.",
      aiLink2Label: "Playbook Maker",
      aiLink2Desc: "Organiseer al je spelen in één digitaal playbook.",
      aiLink3Label: "Gratis Spelenbibliotheek",
      aiLink3Desc: "Blader door 35+ kant-en-klare basketbalspelen.",

      // PlaybookCreatorPage
      pbDigital: "Digitaal Playbook",
      pbHeroTitle: "Basketbal",
      pbHeroAccent: "Playbook",
      pbHeroTitle2: "Maker",
      pbHeroDesc: "Bouw een compleet digitaal basketbalplaybook — teken je spelen, organiseer ze per categorie en deel het volledige playbook met je team met één tik.",
      pbCreateFree: "Maak je Playbook Gratis",
      pbFreePlan: "Gratis plan beschikbaar · Geen download vereist · Werkt op iOS, Android en web",
      pbWholeTitle: "Je hele systeem",
      pbWholeAccent: "op één plek.",
      pbF1Title: "Organiseer op Categorie",
      pbF1Desc: "Sorteer spelen in aanval, verdediging, BLOB, SLOB en einde van wedstrijd. Vind elk spel in seconden.",
      pbF2Title: "Teken op het Coachbord",
      pbF2Desc: "Elk spel wordt getekend op een interactief halfveld met standaard basketbalsymbolen.",
      pbF3Title: "AI-ondersteunde Aanmaak",
      pbF3Desc: "Beschrijf een spel in tekst en AI genereert het diagram. Verfijn het op het bord.",
      pbF4Title: "Toewijzen aan Spelers",
      pbF4Desc: "Tag spelen aan specifieke spelers of posities zodat iedereen zijn rol kent.",
      pbF5Title: "Delen met je Team",
      pbF5Desc: "Stuur het volledige playbook of individuele spelen naar spelers via de app of als exports.",
      pbF6Title: "Privé en Veilig",
      pbF6Desc: "Je playbook is standaard privé. Deel alleen wat je wilt, met wie je wilt.",
      pbStepsTitle: "Bouw je playbook",
      pbStepsAccent: "in 4 stappen.",
      pbStep1Title: "Definieer je systeem",
      pbStep1Desc: "Kies je offensieve en defensieve basis. Je playbook moet één duidelijke tactische identiteit weerspiegelen.",
      pbStep2Title: "Teken je 10 kernspelen",
      pbStep2Desc: "Begin met de spelen die je elke wedstrijd speelt: 2-3 halfveld-sets, 1 BLOB, 1 SLOB, 1 quick hitter. Beheers die voordat je er meer toevoegt.",
      pbStep3Title: "Voeg coachingsnotities toe aan elk spel",
      pbStep3Desc: "Elk spel krijgt 3-5 coachingspunten die de belangrijkste leesopdrachten en beslissingspunten uitleggen.",
      pbStep4Title: "Deel met je team",
      pbStep4Desc: "Stuur het volledige playbook naar je selectie. Spelers kunnen spelen bekijken voor de training en ze raadplegen tijdens wedstrijden.",
      pbLearnMore: "Meer weten",
      pbLink1Label: "Hoe maak je een Playbook",
      pbLink1Desc: "Stapsgewijze gids voor het bouwen van je eerste playbook.",
      pbLink2Label: "Coachbord",
      pbLink2Desc: "Teken spelen op het interactieve digitale veld.",
      pbLink3Label: "Gratis Spelenbibliotheek",
      pbLink3Desc: "Blader door 35+ basketbalspelen om aan je playbook toe te voegen.",
    }
  };

  return strings[lang];
};
